import { useState } from "react";

import { css } from "@compiled/react";
import { FormattedMessage } from "react-intl";

import { LoadingSpinner } from "@/components/global-nav/user-profile-new/src/components/spinner";

import {
    CCPOfferingContent,
    useCCPPriceFinderData,
    useHAMSPriceData
} from "../../../../../contentful";
import {
    COUNTRY_CODE_TO_CURRENCY,
    UserLocale,
    useGeoLocation
} from "../../../../../utils/useGeoCurrency";

import {
    CurrencyISOCode,
    DEFAULT_COUNTRY_ISO_CODE,
    DEFAULT_CURRENCY_ISO_CODE,
    OfferingHamsMap,
    OfferingSKUMap,
    OndemandProductKey,
    PriceFinderMessages,
    PricingPlans,
    ProductKey,
    ProductPlan
} from "./constants";
import { useBoundingClientRectRef } from "./hooks/use-bounding-client-rect-ref";
import { PriceCardsContainer } from "./price-card/card-container";
import { HighestDisplayPriceFontSize } from "./price-card/highest-display-price-font-size";
import { PriceFinderInputs } from "./price-finder-inputs/price-finder-inputs";
import { PriceFinderContainer, PriceFinderState } from "./store/store";
import {
    usePriceFinderRequests,
    usePricing
} from "./utils/use-price-finder-data";

type PriceFinderProps<P extends ProductKey> = {
    hamsProductKey?: OndemandProductKey<P> | "";
    productPlans: ProductPlan<P>[];

    signupURL?: string;
    halpSignupURLs?: string[];
    contactURL?: string;

    countryCode?: UserLocale;
    localeISOCode?: string;
    ccpPricingData: OfferingSKUMap<P, CCPOfferingContent>;
    hamsPricingData: OfferingHamsMap<P, PricingPlans>;

    defaultUnitCount?: number;

    pricingExampleUnitCounts?: number[];
    hideBillingSelector?: boolean;
    freeLimitCount?: number;
    enterpriseMinimumCount?: number;
    showFirstFlatRateTier?: boolean;
    isMonthlyBillingOnly?: boolean;

    messages: PriceFinderMessages;
    dataProductKey: P;
};

export const PriceFinder = <P extends ProductKey>({
    hamsProductKey = "",
    productPlans,
    signupURL = "/try/cloud/signup",
    contactURL = "/enterprise/contact",
    ccpPricingData,
    hamsPricingData,
    defaultUnitCount = 10,
    pricingExampleUnitCounts = [300, 250],
    freeLimitCount = 10,
    enterpriseMinimumCount = 801,
    countryCode = DEFAULT_COUNTRY_ISO_CODE,
    localeISOCode = "en-US",
    isMonthlyBillingOnly = false,
    showFirstFlatRateTier = false,
    messages,
    dataProductKey
}: PriceFinderProps<P>) => {
    const [showPricingExample, setShowPricingExample] = useState(false);
    const [fontSize, setFontSize] = useState("100%");

    const [cardsContainerRect, cardRef] = useBoundingClientRectRef();

    const cardWidth = cardsContainerRect
        ? Math.floor(cardsContainerRect.width / productPlans.length) * 0.75
        : -1;

    const currencyISOCode = (COUNTRY_CODE_TO_CURRENCY[countryCode] ||
        DEFAULT_CURRENCY_ISO_CODE) as CurrencyISOCode;

    const initialFetchPriceCalcs = usePriceFinderRequests<P>(
        pricingExampleUnitCounts,
        hamsProductKey,
        productPlans,
        enterpriseMinimumCount,
        defaultUnitCount,
        "MONTHLY"
    );

    const initialPriceCalcs = usePricing(
        ccpPricingData,
        hamsPricingData,
        initialFetchPriceCalcs,
        currencyISOCode,
        localeISOCode
    );

    const initialState: PriceFinderState = {
        unitCount: defaultUnitCount,
        billingCycle: "MONTHLY",
        priceCalcs: initialPriceCalcs,
        exampleMonthlyUnitCount: pricingExampleUnitCounts[0] as number,
        exampleAnnualUnitCount: pricingExampleUnitCounts[1] as number,
        currencyISOCode: currencyISOCode,
        showFirstFlatRateTier,
        messages: messages
    };

    return (
        <PriceFinderContainer {...initialState}>
            <div css={css({ padding: "16px" })}>
                <HighestDisplayPriceFontSize
                    cardWidth={cardWidth}
                    onFontSizeHandler={setFontSize}
                />
                <h2
                    css={css({
                        "fontSize": "1.25rem",
                        "fontWeight": 500,
                        "lineHeight": "24px",
                        "margin": "32px 0",
                        "textAlign": "center",
                        "@media (min-width: 1024px)": {
                            fontSize: "36px",
                            lineHeight: "44px"
                        }
                    })}
                >
                    <FormattedMessage
                        id="Pricing.priceFinder.comparePlansAndPricing"
                        defaultMessage="Compare plans and pricing"
                    />
                </h2>
                <PriceFinderInputs
                    isMonthlyBillingOnly={isMonthlyBillingOnly}
                    showPricingExample={showPricingExample}
                    setShowPricingExample={setShowPricingExample}
                />

                <PriceCardsContainer<P>
                    cardRef={cardRef}
                    productKey={hamsProductKey}
                    productPlans={productPlans}
                    signupURL={signupURL}
                    contactURL={contactURL}
                    countryCode={countryCode}
                    freeLimitCount={freeLimitCount}
                    enterpriseMinimumCount={enterpriseMinimumCount}
                    fontSize={fontSize}
                    hamsProductKey={hamsProductKey}
                    hamsPricingData={hamsPricingData}
                    ccpPricingData={ccpPricingData}
                    localeISOCode={localeISOCode}
                    dataProductKey={dataProductKey}
                />
            </div>
        </PriceFinderContainer>
    );
};

const PriceFinderWithFetch = <P extends ProductKey>({
    envSource,
    productPlans,
    hamsProductKey,
    messages,
    dataProductKey,
    enterpriseMinimumCount,
    defaultUnitCount
}: {
    envSource: NodeJS.ProcessEnv | Window;
    productPlans: ProductPlan<P>[];
    hamsProductKey: OndemandProductKey<P>;
    messages: PriceFinderMessages;
    dataProductKey: P;
    enterpriseMinimumCount?: number;
    defaultUnitCount?: number;
}) => {
    const offeringSkus = productPlans
        .map((plan) => {
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            return plan.offeringSku!;
        })
        .filter(Boolean);

    const { prices, status } = useCCPPriceFinderData(
        offeringSkus,
        // @ts-ignore
        envSource["PRODUCT_CATALOG_DELIVERY_KEY"] as string,
        // @ts-ignore
        envSource["PRODUCT_CATALOG_SPACE"] as string
    );

    const { prices: hamsPrices, status: hamsStatus } = useHAMSPriceData(
        hamsProductKey,
        // @ts-ignore
        envSource["PRODUCT_CATALOG_DELIVERY_KEY"] as string,
        // @ts-ignore
        envSource["PRODUCT_CATALOG_SPACE"] as string
    );

    const { countryCode, status: locationStatus } = useGeoLocation(
        // @ts-ignore
        envSource["PRODUCT_CATALOG_LOCATION_BASE_URL"]
    );

    if (
        locationStatus == "complete" &&
        status == "complete" &&
        hamsStatus == "complete" &&
        prices &&
        hamsPrices &&
        countryCode
    ) {
        return (
            <PriceFinder
                productPlans={productPlans}
                ccpPricingData={prices}
                hamsPricingData={hamsPrices}
                hamsProductKey={hamsProductKey}
                messages={messages}
                countryCode={countryCode}
                dataProductKey={dataProductKey}
                enterpriseMinimumCount={enterpriseMinimumCount}
                defaultUnitCount={defaultUnitCount}
            />
        );
    }
    return (
        <div
            css={css({
                display: "flex",
                justifyContent: "center",
                height: "900px",
                alignItems: "center"
            })}
        >
            {LoadingSpinner}
        </div>
    );
};

export interface PriceFinderSectionProps<P extends ProductKey> {
    productPlans: ProductPlan<P>[];
    hamsProductKey: OndemandProductKey<P>;
    serverSideCCPPricing?: OfferingSKUMap<P, CCPOfferingContent>;
    serverSideHAMSPricing?: OfferingHamsMap<P, PricingPlans>;
    countryCode?: UserLocale;
    messages: PriceFinderMessages;
    dataProductKey: P;
    enterpriseMinimumCount?: number;
    defaultUnitCount?: number;
}

const PriceFinderSection = <P extends ProductKey>({
    productPlans,
    hamsProductKey,
    serverSideCCPPricing,
    serverSideHAMSPricing,
    countryCode,
    messages,
    dataProductKey,
    enterpriseMinimumCount = 801,
    defaultUnitCount
}: PriceFinderSectionProps<P>) => {
    if (serverSideCCPPricing && serverSideHAMSPricing && countryCode) {
        return (
            <PriceFinder
                productPlans={productPlans}
                ccpPricingData={serverSideCCPPricing}
                hamsPricingData={serverSideHAMSPricing}
                hamsProductKey={hamsProductKey}
                countryCode={countryCode}
                messages={messages}
                dataProductKey={dataProductKey}
                enterpriseMinimumCount={enterpriseMinimumCount}
                defaultUnitCount={defaultUnitCount}
            />
        );
    }
    // server-side request failed
    if (typeof window !== "undefined") {
        // client side render
        return (
            <PriceFinderWithFetch
                productPlans={productPlans}
                hamsProductKey={hamsProductKey}
                messages={messages}
                envSource={window}
                dataProductKey={dataProductKey}
                enterpriseMinimumCount={enterpriseMinimumCount}
                defaultUnitCount={defaultUnitCount}
            />
        );
    }
    // server side render
    return (
        <PriceFinderWithFetch
            productPlans={productPlans}
            hamsProductKey={hamsProductKey}
            envSource={process.env}
            messages={messages}
            dataProductKey={dataProductKey}
            enterpriseMinimumCount={enterpriseMinimumCount}
            defaultUnitCount={defaultUnitCount}
        />
    );
};

export default PriceFinderSection;
